import { useCallback, useRef } from 'react';

import { useRouter } from 'next/router';

import { Box } from '@zillow/constellation-10';

import { useRentalManagerUserPropertyEditV2WebApiHandler_isApplicationsAccepted } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useGetListingDetails } from '../../../hooks/queries/properties';
import { useApplicationsFeeField } from '../../../hooks/useApplicationsFeeField';
import { useFieldSave } from '../../../hooks/useFieldSave';
import { usePropertyEditMutation } from '../../../hooks/usePropertyEditMutation';
import propertyUtils from '../../../utils/propertyUtils';

import ApplicationFeeFormField from './ApplicationFeeFormField';
import ApplicationsFormField from './index';

const inputName = 'isApplicationsAccepted';

const AcceptZillowApplicationsFormField = () => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const isRoomForRent = propertyUtils.isRoomForRent({ listing: listingDetails });
  const isMultifamily = listingDetails?.isMultifamily ?? false;

  const initialValue =
    listingDetails?.isApplicationsAccepted != null ? String(listingDetails.isApplicationsAccepted) : '';

  const { canAccessSFCostsAndFees, feeAmount, feeError, setFeeError, validateFeeAmount, handleFeeChange } =
    useApplicationsFeeField(listingAlias, { isMultifamily, isFeed: false });

  // Ref so buildMutationVariables always captures the latest fee amount without recreating useFieldSave
  const feeAmountRef = useRef(feeAmount);
  feeAmountRef.current = feeAmount;

  const updateMutation = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditV2WebApiHandler_isApplicationsAccepted,
    applyToCache: true,
    meta: {
      invalidates: ['sf-applicationFee'],
      context: { api: 'RentalManagerUserPropertyEditV2WebApiHandler_isApplicationsAccepted' },
    },
  });

  const buildMutationVariables = useCallback(
    (value: string) => {
      const params: {
        propertyId: string;
        isApplicationsAccepted: boolean;
        inviteOnly: boolean;
        applicationFeeAmount?: number;
      } = {
        propertyId: listingAlias,
        isApplicationsAccepted: value === 'true',
        inviteOnly: false,
      };

      if (canAccessSFCostsAndFees && value === 'false' && feeAmountRef.current !== '') {
        params.applicationFeeAmount = Number(feeAmountRef.current);
      }

      return { postData: params };
    },
    [listingAlias, canAccessSFCostsAndFees],
  );

  const field = useFieldSave({
    fieldName: inputName,
    initialValue,
    mutation: updateMutation,
    buildMutationVariables,
    autoSaveOnChange: true,
  });

  const handleFeeBlur = () => {
    if (!canAccessSFCostsAndFees || field.value !== 'false') return;
    const err = validateFeeAmount(feeAmountRef.current);
    if (err) {
      setFeeError(err);
      return;
    }
    // Errors surface through field.error, so the rejection has no other listener.
    void updateMutation.mutateAsync(buildMutationVariables(field.value)).catch(() => undefined);
  };

  return (
    <>
      <ApplicationsFormField
        radioValue={field.value}
        onRadioChange={(e) => field.onChange((e.target as HTMLInputElement).value)}
        serverError={field.error ?? undefined}
        isRoomForRent={isRoomForRent}
        isCompact={true}
      />

      {canAccessSFCostsAndFees && field.value === 'false' && (
        <Box css={{ paddingLeft: '800', marginTop: '400' }}>
          <ApplicationFeeFormField
            value={feeAmount}
            onChange={handleFeeChange}
            onBlur={handleFeeBlur}
            error={feeError}
          />
        </Box>
      )}
    </>
  );
};

export default AcceptZillowApplicationsFormField;
